import {
  useWt,
  WuSidebar,
  WuSidebarContent,
  WuSidebarFooter,
  WuSidebarGroup,
  WuSidebarItem,
  WuSidebarMenu,
} from '@npm-questionpro/wick-ui-lib'
import {Suspense, useEffect} from 'react'
import {NavLink, Outlet, useLocation} from 'react-router'
import {cx, getIsAdminUI} from '../../utils'
import {GLOBAL} from '../../Translation/English/global'
import styles from './MainLayout.module.scss'
import {userSelectors} from '../../store/user/userSelectors'
import {LoadingPage} from '../../screens/LoadingPage'
import {
  preloadSidebarRoutesOnIdle,
  routePreload,
} from '../../routes/routePreload'

interface ISidebarContentProps {
  currentUser: {isAdmin?: boolean} | null | undefined
  isAdminUI: boolean
  getIsActiveTab: (tab: string) => boolean
}

/**
 * Start fetching a route's chunk before the click lands. `lazy()` shares the
 * same thunk, so by click time the module is usually already resolved and the
 * route commits without suspending.
 */
const prefetch = (load: () => Promise<unknown>) => (): void => {
  void load()
}

const SidebarContent = ({
  currentUser,
  isAdminUI,
  getIsActiveTab,
}: ISidebarContentProps): React.ReactElement => {
  const wt = useWt()

  return (
    <>
      <WuSidebarContent
        className={!isAdminUI ? styles.portalSidebarContent : ''}
      >
        <WuSidebarMenu>
          <WuSidebarItem
            Icon={<span className="wm-home" />}
            isActive={getIsActiveTab('/') || getIsActiveTab('/home')}
            className={cx(!isAdminUI && styles.portalSidebar)}
          >
            <NavLink
              to="/home"
              onMouseEnter={prefetch(routePreload.home)}
              onFocus={prefetch(routePreload.home)}
            >
              {wt(GLOBAL.home)}
            </NavLink>
          </WuSidebarItem>

          <WuSidebarGroup label={wt(GLOBAL.edit)}>
            <WuSidebarItem
              Icon={<span className="wm-dashboard" />}
              isActive={getIsActiveTab('/initiatives')}
              className={cx(!isAdminUI && styles.portalSidebar)}
            >
              <NavLink
                to="/initiatives"
                onMouseEnter={prefetch(routePreload.initiatives)}
                onFocus={prefetch(routePreload.initiatives)}
              >
                {wt(GLOBAL.initiatives)}
              </NavLink>
            </WuSidebarItem>
            <WuSidebarItem
              Icon={<span className="wc-hierarchy" />}
              isActive={getIsActiveTab('/team-view')}
              className={cx(!isAdminUI && styles.portalSidebar)}
            >
              <NavLink
                to="/team-view"
                onMouseEnter={prefetch(routePreload.teamView)}
                onFocus={prefetch(routePreload.teamView)}
              >
                {wt(GLOBAL.teamView)}
              </NavLink>
            </WuSidebarItem>
          </WuSidebarGroup>

          <WuSidebarGroup label={wt(GLOBAL.data)}>
            <WuSidebarItem
              Icon={<span className="wm-bar-chart" />}
              isActive={getIsActiveTab('/analytics')}
              className={cx(!isAdminUI && styles.portalSidebar)}
            >
              <NavLink
                to="/analytics"
                onMouseEnter={prefetch(routePreload.analytics)}
                onFocus={prefetch(routePreload.analytics)}
              >
                {wt(GLOBAL.analytics)}
              </NavLink>
            </WuSidebarItem>
            <WuSidebarItem
              Icon={<span className="wm-forum" />}
              isActive={getIsActiveTab('/conversations')}
              className={cx(!isAdminUI && styles.portalSidebar)}
            >
              {/* Dialogs is an eager import — nothing to prefetch. */}
              <NavLink to="/conversations">{wt(GLOBAL.conversations)}</NavLink>
            </WuSidebarItem>
          </WuSidebarGroup>
        </WuSidebarMenu>
      </WuSidebarContent>

      <WuSidebarFooter className={styles.sidebarFooter}>
        <WuSidebarMenu>
          {isAdminUI && currentUser?.isAdmin && (
            <WuSidebarItem
              Icon={<span className="wm-shield-person" />}
              isActive={getIsActiveTab('/admin')}
              className={cx(!isAdminUI && styles.portalSidebar)}
            >
              <NavLink
                to="/admin"
                onMouseEnter={prefetch(routePreload.admin)}
                onFocus={prefetch(routePreload.admin)}
              >
                {wt(GLOBAL.admin)}
              </NavLink>
            </WuSidebarItem>
          )}
          <WuSidebarItem
            Icon={<span className="wm-settings" />}
            isActive={getIsActiveTab('/settings')}
            className={cx(!isAdminUI && styles.portalSidebar)}
          >
            <NavLink
              to="/settings"
              onMouseEnter={prefetch(routePreload.settings)}
              onFocus={prefetch(routePreload.settings)}
            >
              {wt(GLOBAL.settings)}
            </NavLink>
          </WuSidebarItem>
        </WuSidebarMenu>
      </WuSidebarFooter>
    </>
  )
}

/**
 * Layout route for every sidebar destination. It is mounted once above the
 * screen routes (not per route element), so the sidebar keeps its open/closed
 * state across navigations and — because the active tab is derived from
 * useLocation() — the highlight moves on the same commit as the URL, even while
 * the destination chunk is still loading behind the Suspense boundary below.
 */
export const MainLayout = (): React.JSX.Element => {
  const currentUser = userSelectors.useCurrentUser()
  const isAdminUI = getIsAdminUI()
  const {pathname} = useLocation()

  useEffect(() => preloadSidebarRoutesOnIdle(), [])

  const getIsActiveTab = (tab: string): boolean => {
    const regex = new RegExp(`^${tab}(\\/.*)?$`)
    return regex.test(pathname)
  }

  return (
    <WuSidebar
      defaultOpen={false}
      Sidebar={
        <SidebarContent
          currentUser={currentUser}
          isAdminUI={isAdminUI}
          getIsActiveTab={getIsActiveTab}
        />
      }
    >
      <div className={styles.content}>
        <div className={styles.children}>
          <Suspense fallback={<LoadingPage />}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </WuSidebar>
  )
}
